/**
 * SkuCard, Sprint 1.5 chat widget recommendation card.
 *
 * Rendered inline within ChatBubble (carousel variant, horizontally
 * scrollable) and inside VendorGridOverlay (grid variant, masonry-like).
 *
 * Per Lucky's directive (2026-05-13): metadata is DYNAMIC. The LLM
 * chooses 0-2 highlight_fields per SKU based on what's actually
 * relevant to the user's brief and the SKU's category. A spa voucher
 * gets {Validity, Treatment time}. A cake gets {Lead time, Serves}.
 * A workshop gets {Duration, Group size}. We don't hardcode the
 * meta, the LLM picks it on a per-card basis.
 *
 * MS61a (2026-05-16): CTA changed from "Send →" to "View →" per Lucky.
 * The card no longer triggers an order flow directly — that was the
 * wrong default. A buyer needs to SEE the product (description,
 * voucher terms, vendor context, size/date selection eventually)
 * before committing to send. The "View" callback opens the
 * ProductDetailModal in the parent surface; the actual "Send" CTA
 * now lives INSIDE that modal, after the buyer has reviewed the
 * full product context. Pre-MS61a behaviour ("Send" goes straight
 * to OrderConfirmSheet) was hostile to the buyer's decision process,
 * especially for vouchers where they have no idea what the voucher
 * covers without context.
 *
 * Pioneer alignment:
 *  - Pablo Stanley: alt text on every image (auto-generated from
 *    name + vendor + category); placeholder gradient when image_url
 *    is null so the card never breaks.
 *  - Norman: "View →" is the obvious primary action. Card-level click
 *    falls through to it. The View → Send progression is the
 *    "preview then commit" pattern (Krug 5: don't make me commit
 *    without seeing what I'm committing to).
 *  - Wroblewski: touch targets ≥ 44px (the View button is 44px).
 */

import { useRef } from "react";
import { ArrowRight, Heart, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useWishlistSkuIds, useWishlistMutations } from "@/hooks/queries";
import { toast } from "sonner";
// 2026-05-16 UX-copy audit: vendor / "Voucher" / "$N" tokens were
// doubling up between the title and the price line. Centralised
// dedupe lives in @/lib/formatSkuName now.
import { formatSkuName, formatSkuPrice } from "@/lib/formatSkuName";
import { captureInteraction, type InteractionSurface } from "@/lib/captureInteraction";

export interface SkuCardData {
  sku_id: string;
  name: string;
  vendor_name: string;
  vendor_category: string;
  price_aud: number;
  image_url: string | null;
  slug: string;
  description: string | null;
  is_voucher: boolean;
  why_this_one?: string;
  highlight_fields: Array<{ label: string; value: string }>;
  // MS312: lead-time arc. Promoted from the LLM-chosen highlight_fields
  // string to a typed numeric so OrderConfirmSheet can do real date
  // arithmetic (block customers from picking a delivery date that's
  // before today + lead_time_days). highlight_fields stays the
  // human-readable surface; this field is for validation logic.
  // Null = unknown lead time (treat as 0 / same-day in validation).
  lead_time_days?: number | null;
  // MS318: dietary-conflict arc. Pulled onto the base card so
  // OrderConfirmSheet can cross-check the recipient's known dietary
  // requirements (from team_members) against what the SKU's tagged.
  // Lowercase snake_case per MS315 normalization. Undefined when
  // the source hydration didn't select it (treated as "unknown" —
  // the warning still fires informationally so the customer
  // verifies with the vendor).
  dietary_tags?: string[] | null;
  /** MS492: true when this row is a multi-tier voucher parent. */
  price_prefix_from?: boolean;
}

interface SkuCardProps {
  sku: SkuCardData;
  variant: "carousel" | "grid";
  /** MS61a: was `onSend`. Renamed to reflect the new semantics —
   *  clicking the CTA opens a ProductDetailModal in the parent
   *  surface; the actual "Send to recipient" CTA lives inside that
   *  modal AFTER the buyer reviews the product. */
  onView: (sku: SkuCardData) => void;
  /** MS540: render the image eagerly + fetchpriority="high" for cards
   *  the caller knows are above the fold on first paint. Without this,
   *  `loading="lazy"` defers the fetch until the IntersectionObserver
   *  fires, which on this app reliably leaves the first row of /browse
   *  and the entire /chat recommendation row painted as blank gray
   *  placeholders for 4-10s — even though the image URLs are valid
   *  and the bytes eventually download. Callers should pass `true`
   *  only for cards visible without scrolling. */
  priority?: boolean;
  /** MS540: surface name for image-load telemetry. SkuCard is used in
   *  /browse, /chat, /vendor, /dashboard — knowing which surface owns
   *  a slow/broken thumbnail lets us triage by impact. Defaults to
   *  `browse` because that's where the regression originally landed
   *  (audit AUDIT-029); pass `chat` from concierge surfaces. */
  surface?: InteractionSurface;
}

// Stable gradient choice based on the sku_id hash, same SKU always
// gets the same gradient, so a card without an image still has a
// distinct visual identity. Per Pablo Stanley: defaults matter.
const GRADIENTS = [
  "from-butter to-butter/40",
  "from-coral/30 to-butter",
  "from-navy/10 to-butter/60",
  "from-coral/20 to-navy/10",
  "from-butter/80 to-coral/15",
];

function gradientForId(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
}

// 2026-05-16 UX-copy audit: local formatPrice replaced by the
// shared formatSkuPrice from @/lib/formatSkuName. Kept this file
// free of the duplicate so ProductDetailModal + OrderConfirmSheet
// can't drift independently next time we tweak the price line.

function altTextFor(sku: SkuCardData): string {
  // Per Stanley: alt text describes the image's role in the page.
  // Auto-generated from the data we have so no SKU ships without alt.
  return `${sku.name}, ${sku.vendor_category} from ${sku.vendor_name}`;
}

export function SkuCard({
  sku,
  variant,
  onView,
  priority = false,
  surface = "browse",
}: SkuCardProps) {
  const carousel = variant === "carousel";
  // MS196 + MS-hero-mobile: carousel cards size as a PERCENTAGE of the
  // scroll container on mobile (not fixed px), so on any phone width the
  // widget shows one roomy card plus a clear ~22% peek of the next — the
  // peek is what cues the carousel as scrollable, and a percentage keeps
  // that ratio consistent from a 320px phone to a 430px one (a fixed
  // 210px read as a single static card on the narrowest devices and left
  // dead space on the widest). Desktop pins a fixed 240px where the bubble
  // is wide enough to show 2+ cards regardless.
  const widthCls = carousel
    ? "min-w-[78%] max-w-[78%] sm:min-w-[240px] sm:max-w-[240px] snap-start"
    : "w-full";
  // MS-hero-mobile: carousel cards use a shorter landscape image on mobile
  // (4/3) so a whole card — image, name, price, View — fits inside the chat
  // widget's message area (~433px on a 667px Safari viewport) without the
  // user scrolling within the thread just to see one recommendation. The
  // tall 3/4 portrait made a single card ~515px, taller than the area. sm+
  // (wider bubble, more height) keeps the editorial 3/4 portrait.
  const aspectCls = carousel ? "aspect-[4/3] sm:aspect-[3/4]" : "aspect-square";

  // MS540: image-load telemetry. We measure from mount, not from when
  // the `<img>` starts fetching (the browser doesn't expose that), so
  // the timing is "how long after the card rendered did the photo
  // become visible." Only fire the slow event past a threshold to
  // keep interaction_captures from filling up with the happy path.
  const imageMountedAt = useRef<number>(
    typeof performance !== "undefined" ? performance.now() : 0,
  );
  const imageReported = useRef(false);
  const onImageLoad = () => {
    if (imageReported.current) return;
    imageReported.current = true;
    const elapsed_ms = Math.round(performance.now() - imageMountedAt.current);
    if (elapsed_ms < 1500) return;
    void captureInteraction({
      surface,
      event_type: "image_load_slow",
      vendor_product_id: sku.sku_id,
      payload: { image_url: sku.image_url, elapsed_ms, priority, variant },
    });
  };
  const onImageError = () => {
    if (imageReported.current) return;
    imageReported.current = true;
    void captureInteraction({
      surface,
      event_type: "image_load_error",
      vendor_product_id: sku.sku_id,
      payload: {
        image_url: sku.image_url,
        elapsed_ms: Math.round(performance.now() - imageMountedAt.current),
        priority,
        variant,
      },
    });
  };

  // Sprint 2.2c, heart toggle for authed users. Anon visitors don't
  // see the heart; they get the magic-link signup CTA on /order/success
  // after purchase, which converts them to authed for future saves.
  const { user } = useAuth();
  const wishlistIdsQuery = useWishlistSkuIds(user?.id);
  const { add, remove } = useWishlistMutations(user?.id);
  // MS93: migrated from shadcn useToast to sonner. The save-with-undo
  // action now uses sonner's structured action shape ({ label, onClick })
  // — cleaner than embedding a React element in the toast options.
  const isSaved = wishlistIdsQuery.data?.has(sku.sku_id) ?? false;
  const isToggling = add.isPending || remove.isPending;
  const showHeart = Boolean(user);

  // Sprint 2.5a B.3 P1-A, toast feedback on heart toggle so the user
  // gets an explicit "saved" affirmation instead of relying on the
  // visual color swap (which is easy to miss). Includes Undo so the
  // un-toggle isn't buried in a second tap on the same heart. Norman:
  // visible feedback proportionate to the commitment + reversibility
  // for any commit action.
  const onHeartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user || isToggling) return;
    if (isSaved) {
      remove.mutate(sku.sku_id);
      toast("Removed from saved", {
        description: `${sku.name} is no longer in your saved gifts.`,
      });
    } else {
      add.mutate(sku.sku_id);
      toast.success("Saved", {
        description: `${sku.name} added to your saved gifts.`,
        action: {
          label: "Undo",
          onClick: () => remove.mutate(sku.sku_id),
        },
      });
    }
  };

  return (
    <div
      className={`${widthCls} flex h-full flex-col overflow-hidden rounded-2xl border-2 border-navy/10 bg-white shadow-sm`}
      style={{ fontFamily: "DM Sans, sans-serif" }}
    >
      {/* Image / placeholder */}
      <div className={`relative ${aspectCls} w-full overflow-hidden bg-charcoal/5`}>
        {sku.image_url ? (
          <img
            src={sku.image_url}
            alt={altTextFor(sku)}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            onLoad={onImageLoad}
            onError={onImageError}
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            aria-label={altTextFor(sku)}
            role="img"
            className={`h-full w-full bg-gradient-to-br ${gradientForId(sku.sku_id)} flex items-end p-3`}
          >
            <span
              className="text-xs font-medium uppercase tracking-wider text-navy/55"
              style={{ letterSpacing: "0.1em" }}
            >
              {sku.vendor_category}
            </span>
          </div>
        )}

        {/* Sprint 2.2c, Heart toggle. Sits top-right of the image, white
            backdrop so it's legible over any photo. Anon-hidden. */}
        {showHeart && (
          <button
            type="button"
            onClick={onHeartClick}
            disabled={isToggling}
            aria-label={isSaved ? `Remove ${sku.name} from saved` : `Save ${sku.name}`}
            aria-pressed={isSaved}
            className={`absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur-sm transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2 ${
              isToggling ? "opacity-60" : ""
            }`}
          >
            {isToggling ? (
              <Loader2 size={14} className="animate-spin text-charcoal/55" />
            ) : (
              <Heart
                size={15}
                strokeWidth={2}
                className={
                  isSaved
                    ? "fill-coral text-coral"
                    : "text-charcoal/55"
                }
              />
            )}
          </button>
        )}
      </div>

      {/* Body — grid cards use a pinned footer CTA so content never overlaps
          the button (flex + mt-auto alone overflows when chips + title fill
          the stretched row height). */}
      <div
        className={`flex flex-1 flex-col ${variant === "grid" ? "p-3" : "p-3.5"} ${
          variant === "grid" ? "min-h-[152px]" : ""
        }`}
      >
        <div
          className={
            variant === "grid"
              ? "flex min-h-0 flex-1 flex-col overflow-hidden"
              : "flex flex-1 flex-col"
          }
        >
          <p
            className="text-xs text-charcoal/55"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {sku.vendor_name}
          </p>
          <h3
            className="mt-0.5 line-clamp-2 text-sm font-semibold leading-tight text-navy"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {formatSkuName({
              name: sku.name,
              vendor_name: sku.vendor_name,
              vendor_category: sku.vendor_category,
              is_voucher: sku.is_voucher,
            })}
          </h3>
          <p
            className="mt-1 text-sm font-semibold text-navy"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {formatSkuPrice(sku.price_aud, sku.is_voucher, {
              prefixFrom: sku.price_prefix_from,
            })}
          </p>

          {/* Dynamic highlight chips — fixed single-row slot on grid cards */}
          <div
            className={
              variant === "grid"
                ? "mt-2 flex h-7 shrink-0 flex-wrap items-start gap-1 overflow-hidden"
                : "mt-2 flex flex-wrap gap-1"
            }
          >
            {(variant === "grid" ? sku.highlight_fields.slice(0, 1) : sku.highlight_fields.slice(0, 2)).map(
              (hf) => (
                <span
                  key={`${hf.label}:${hf.value}`}
                  className="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-charcoal/5 px-2 py-0.5 text-[11px] text-charcoal/75"
                >
                  <span className="shrink-0 font-medium text-navy/75">{hf.label}:</span>
                  <span className="truncate">{hf.value}</span>
                </span>
              ),
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => onView(sku)}
          aria-label={`View ${sku.name} details`}
          className={`${
            variant === "grid" ? "mt-2 shrink-0" : "mt-3"
          } inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-coral text-sm font-semibold text-white transition-colors hover:bg-coral/85 focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2 ${
            variant === "grid" ? "h-9 text-xs" : "h-11"
          }`}
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          View
          <ArrowRight size={variant === "grid" ? 12 : 14} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
